'use client'

import { useState, useEffect } from 'react'
import Link from 'next/link'
import { notFound, useRouter } from 'next/navigation'
import { Project, getFeaturedProjects, getImageUrl, getVideoUrl } from '../../../lib/sanity'
import CinemaProjectView from '@/components/CinemaProjectView'

// Removed revalidate export - not needed for client components

export default function ProjectDetail({ params }: { params: Promise<{ slug: string }> }) {
  const [projects, setProjects] = useState<Project[]>([])
  const [currentProjectIndex, setCurrentProjectIndex] = useState(-1)
  const [currentMediaIndex, setCurrentMediaIndex] = useState(0)
  const [showCinema, setShowCinema] = useState(true)
  const router = useRouter()

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const { slug } = await params
        const fetchedProjects = await getFeaturedProjects()
        setProjects(fetchedProjects)
        const projectIndex = fetchedProjects.findIndex(p => p.id === slug)
        if (projectIndex === -1) {
          notFound()
        }
        setCurrentProjectIndex(projectIndex)
      } catch (error) {
        console.error("Failed to fetch projects:", error)
        notFound()
      }
    }
    fetchProjects()
  }, [params])

  const handleCloseCinema = () => {
    router.push('/work')
  }

  const handleNextMedia = () => {
    const project = projects[currentProjectIndex]
    if (project?.media && currentMediaIndex < project.media.length - 1) {
      setCurrentMediaIndex((prev: number) => prev + 1)
    } else {
      // Move to the next project
      if (currentProjectIndex < projects.length - 1) {
        setCurrentProjectIndex((prev: number) => prev + 1)
      setCurrentMediaIndex(0)
      }
    }
  }

  const handlePrevMedia = () => {
    if (currentMediaIndex > 0) {
      setCurrentMediaIndex((prev: number) => prev - 1)
    } else {
      // Move to the previous project
      if (currentProjectIndex > 0) {
        const prevProject = projects[currentProjectIndex - 1]
        setCurrentProjectIndex((prev: number) => prev - 1)
        setCurrentMediaIndex(prevProject.media ? prevProject.media.length - 1 : 0)
      }
    }
  }

  const getNextProject = () => {
    if (currentProjectIndex < projects.length - 1) {
      return projects[currentProjectIndex + 1]
    }
    return undefined
  }

  const getNextProjectId = () => {
    const nextProject = getNextProject()
    return nextProject?.id
  }

  if (currentProjectIndex === -1) {
    return <div className="fixed inset-0 bg-black flex items-center justify-center text-white"><div className="w-8 h-8 border-2 border-gray-300 border-t-black rounded-full animate-spin"></div></div>
  }

  const project = projects[currentProjectIndex]

  return (
    <>
      {showCinema && (
        <CinemaProjectView
          project={project}
          onClose={handleCloseCinema}
          onNext={handleNextMedia}
          onPrev={handlePrevMedia}
          currentMediaIndex={currentMediaIndex}
          totalMedia={project.media?.length || 0}
          nextProjectId={getNextProjectId()}
          nextProject={getNextProject()}
        />
      )}
      {!showCinema && (
        <div className="p-8">
          <Link href="/work" className="text-blue-500 hover:underline">
            Back to Work
          </Link>
          <h1 className="text-4xl font-bold mt-4">{project.title}</h1>
          {/* Add non-cinema project details here if needed */}
        </div>
      )}
    </>
  )
} 